import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Plus, Minus, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import ProductCard from '../components/ProductCard';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      
      // Fetch similar products
      const similarResponse = await axios.get(`${API}/products`, {
        params: { category: response.data.category }
      });
      setSimilarProducts(similarResponse.data.filter(p => p.id !== id).slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch product:', error);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${product.name} добавлен в корзину`);
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-500">Загрузка...</div>
      </div>
    );
  }

  const isAvailable = product.availability === 'есть';

  return (
    <div className="min-h-screen bg-gray-50 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/catalog" className="inline-flex items-center text-sky-600 hover:text-sky-700 mb-8" data-testid="back-to-catalog">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Назад в каталог
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Image */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl" data-testid="product-image-container">
            <img
              src={product.image_url || 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38'}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Product Info */}
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <div className="mb-4">
              <span className="text-sm font-medium px-3 py-1 bg-sky-100 text-sky-700 rounded-full">
                {product.category}
              </span>
              {product.subcategory && (
                <span className="ml-2 text-sm font-medium px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                  {product.subcategory}
                </span>
              )}
            </div>

            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="product-page-title">
              {product.name}
            </h1>

            <div className="text-4xl font-bold text-sky-600 mb-6" data-testid="product-page-price">
              {product.price.toLocaleString('ru-RU')} ₽
              {product.weight && (
                <span className="text-lg text-gray-500 ml-2">/ {product.weight}</span>
              )}
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-center">
                <span className="font-semibold w-32">Происхождение:</span>
                <span className="text-gray-700">🌍 {product.origin}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold w-32">Наличие:</span>
                <span className={`font-medium ${isAvailable ? 'text-green-600' : 'text-orange-600'}`}>
                  {product.availability}
                </span>
              </div>
              {product.details && (
                <div className="flex items-center">
                  <span className="font-semibold w-32">Детали:</span>
                  <span className="text-gray-700">{product.details}</span>
                </div>
              )}
            </div>

            {product.description && (
              <div className="mb-8 p-6 bg-gray-50 rounded-xl">
                <h3 className="font-semibold mb-2">Описание:</h3>
                <p className="text-gray-700">{product.description}</p>
              </div>
            )}

            {isAvailable && (
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <span className="font-semibold">Количество:</span>
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      data-testid="decrease-quantity"
                      variant="outline"
                      size="icon"
                      className="w-10 h-10"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-2xl font-bold w-12 text-center" data-testid="quantity-display">{quantity}</span>
                    <Button
                      onClick={() => setQuantity(quantity + 1)}
                      data-testid="increase-quantity"
                      variant="outline"
                      size="icon"
                      className="w-10 h-10"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="text-2xl font-bold text-gray-900">
                  Итого: {(product.price * quantity).toLocaleString('ru-RU')} ₽
                </div>

                <Button
                  onClick={handleAddToCart}
                  data-testid="add-to-cart-product-page"
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white h-14 text-lg"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  Добавить в корзину
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Similar Products */}
        {similarProducts.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Похожие товары
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="similar-products">
              {similarProducts.map(p => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}